import { PermissionPageKey } from '@utility/constants/permission';

export type MenuNode = {
  key: string;
  text?: string;
  link?: string;
  genericLink?: string;
  /** URL root of the section, used to place routes that have no menu entry. */
  parentLink?: string;
  /** Set where the section answers for every route beneath it, not just its own. */
  routeRoot?: string;
  permission?: string;
  menus?: MenuNode[];
};

export type MenuMatch = {
  key: string;
  permission?: string;
};

type Entry = MenuMatch & { path: string; sectionRoot: boolean };

type Section = { path: string; children: Entry[] };

export type PermissionIndex = {
  items: Entry[];
  exact: Entry[];
  roots: Entry[];
  sections: Section[];
};

const OBJECT_ID = /[a-f\d]{24}/i;

function trimSlashes(link: string | undefined): string | null {
  const trimmed = link?.replace(/\/+$/, '');
  return trimmed || null;
}

function toEntry(node: MenuNode, path: string): Entry {
  return {
    key: node.key,
    permission: node.permission,
    path,
    sectionRoot: false,
  };
}

/** Menus nest to whatever depth they are written to; a level left out of the
 * index would silently inherit an ancestor's wider permission. */
function collect(node: MenuNode, into: Entry[]): void {
  const path = trimSlashes(node.link);
  if (path) {
    into.push(toEntry(node, path));
  }
  for (const child of node.menus ?? []) {
    collect(child, into);
  }
}

/**
 * Flatten the sidebar into the table route guards search.
 *
 * `items` are the menu entries themselves and answer for anything beneath them,
 * except where one entry sits on the path of another: `/app/scm` is the
 * Purchasing dashboard and also the root of half a dozen siblings, and letting
 * it swallow `/app/scm/assign-items` would hand that page a permission nobody
 * granted it. Those match exactly.
 *
 * `roots` are the sections that answer for every route beneath them — an
 * order's detail page has no menu entry and belongs to Orders all the same.
 * `sections` catch the reverse case, a URL naming a section that only its
 * children fill in, which resolves to the first page under it.
 */
export function buildPermissionIndex(menus: MenuNode[]): PermissionIndex {
  const items: Entry[] = [];
  const exact: Entry[] = [];
  const roots: Entry[] = [];
  const sections: Section[] = [];

  for (const group of menus) {
    const children: Entry[] = [];
    for (const child of group.menus ?? []) {
      collect(child, children);
    }
    items.push(...children);

    const link = trimSlashes(group.link);
    if (link) {
      exact.push(toEntry(group, link));
    }

    const routeRoot = trimSlashes(group.routeRoot);
    if (routeRoot) {
      roots.push(toEntry(group, routeRoot));
    }

    const parentLink = trimSlashes(group.parentLink);
    if (parentLink && children.length) {
      sections.push({ path: parentLink, children });
    }
  }

  for (const item of items) {
    item.sectionRoot = items.some(
      other => other !== item && other.path.startsWith(`${item.path}/`),
    );
  }

  const byDepth = (a: Entry, b: Entry) => b.path.length - a.path.length;

  return {
    items: [...items].sort(byDepth),
    exact,
    roots: roots.sort(byDepth),
    sections,
  };
}

function covers(url: string, path: string): boolean {
  return url === path || url.startsWith(`${path}/`);
}

/**
 * The order settings tabs hang off the Orders page rather than the sidebar, so
 * their permission is read straight from the user.
 */
function orderSettingsPermission(user, url: string): MenuMatch {
  const param = url?.split('?')?.[1] ?? 'tab=Order%20Mapping';
  const { settings } = user?.permissions?.[PermissionPageKey.Orders] ?? {};

  return {
    key: PermissionPageKey.Orders,
    permission:
      param === 'tab=Carrier%20Services'
        ? settings?.['Carrier Services']
        : settings?.['Order Mapping'],
  };
}

export function resolveMenuByUrl(
  index: PermissionIndex,
  user,
  url: string,
  options?: { fullMatch?: boolean },
): MenuMatch | null {
  const { fullMatch = false } = options ?? {};

  let target = url;

  if (fullMatch) {
    if (target.startsWith('/app/orders/settings')) {
      return orderSettingsPermission(user, target);
    }
    if (target.startsWith('/app/catalogs/conflicts')) {
      target = '/app/catalogs/catalog';
    }
  }

  target = target.split('?')[0];
  target = target.replace(OBJECT_ID, '');
  target = target.replace('[id]', '');
  target = target.replace(/\/+$/, '');

  return (
    index.items.find(entry =>
      entry.sectionRoot ? target === entry.path : covers(target, entry.path),
    ) ??
    index.exact.find(entry => entry.path === target) ??
    index.roots.find(entry => covers(target, entry.path)) ??
    (fullMatch
      ? index.sections
          .find(section => target.includes(section.path))
          ?.children.find(child => child.path.startsWith(`${target}/`))
      : undefined) ??
    null
  );
}
